import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';

import { frontendRoutes } from '../Constants/constants';
import { getPropertyInfo } from '../Constants/api';
import { get_property_details } from '../redux/action/action';
import { getPropertyAccessNextPath } from '../utils';
import { useRouteHistory } from '../providers/RouteHistoryProvider';
import { BuilderSettings } from '../redux/types/builders';
import { SurveyNavigationState } from '../types/navigation';

/**
 * Returns an async `redirectAfterAuth(customerId)` function that encodes the
 * canonical post-auth routing (scheduledVisit → nextPath/survey → multipleTour
 * → preferTour).  Reads navigation state from `history.location.state` at
 * call-time so it works correctly from both EmailVerify (login) and TextVerify
 * (catch-up completion).
 */
export const usePostAuthRedirect = () => {
  const params = useParams<{ builder_name?: string }>();
  const history = useHistory();
  const dispatch = useDispatch();
  const routerHistory = useRouteHistory();

  const scheduledVisitId = useSelector((state: any) => state.scheduledVisit.scheduledVisitId);
  const builderSettings: BuilderSettings = useSelector((state: any) => state.builder.settings);
  const tour = useSelector((state: any) => state.tour.tour);
  const property = useSelector((state: any) => state.property.property);

  const getNextPath = (propertyBuilder?: string | null): string | undefined => {
    const navState = history.location.state as SurveyNavigationState | undefined;
    if (navState?.nextPath) return navState.nextPath;
    if (!tour) return undefined;
    return getPropertyAccessNextPath(
      params.builder_name,
      builderSettings.geolocation_active,
      propertyBuilder,
    );
  };

  return async (customerId: number): Promise<void> => {
    if (scheduledVisitId) {
      history.push(
        `${frontendRoutes.cancelTour(params.builder_name).path}?scheduledVisitId=${scheduledVisitId}`,
      );
      return;
    }

    const nextPath = getNextPath(property?.builder);

    if (nextPath) {
      if (property?.id) {
        const propertyInfo = await getPropertyInfo(property.id, customerId);
        const propertyDetails = propertyInfo ? { id: property.id, ...propertyInfo } : property;

        if (propertyInfo) {
          dispatch(get_property_details(propertyDetails));
        }

        if (!propertyDetails?.didCompleteSurveyForBuilder) {
          history.push(frontendRoutes.surveyQuestions(params.builder_name).path, { nextPath });
          return;
        }
      }

      history.push(nextPath);
      return;
    }

    const isMultipleTour = localStorage.getItem('is_multiple_tour');
    if (isMultipleTour === 'true') {
      const path = routerHistory.history[routerHistory.history.length - 3];
      const communityId = path.split('/').pop();
      history.push(frontendRoutes.community(params.builder_name, communityId).path);
      return;
    }

    history.push(frontendRoutes.preferTour(params.builder_name).path);
  };
};
